import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { deleteDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useAuth } from "./AuthContext";
import { db, functions } from "../firebase";
import { httpsCallable } from 'firebase/functions';
import { trackedGetDoc, invalidateFirestoreReadCache } from "../utils/firestoreReadCache";
import { refreshEmployeeAccess, EMPLOYEE_PROVISIONING_V2 } from '../utils/employeeAccess';
import {
  isAccountStatusUnlocked,
  normalizeAccountStatus,
  normalizeBillingPlan,
  LEGACY_FEATURE_ALIASES,
  getFeatureAccess,
  hasFeatureAccess,
  normalizeFeatureMap,
  normalizePlan,
  resolveAccessPlan,
} from "../utils/featureAccess";
import {
  DEFAULT_STAFF_LIMIT,
  normalizeExtraStaffPurchased,
  normalizeStaffLimit,
} from "../utils/teamAccess";
import { IS_DEV_UI_ENABLED, devLog } from "../utils/devUi";
import { canonicalMemberPermissions, canonicalMemberRole } from "../utils/accountMemberPermissions";

const AccountContext = createContext(null);

export function useAccount() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error("useAccount must be used inside AccountProvider");
  }
  return context;
}

function normalizeUserRoot(userDoc = {}, authUser) {
  const activeAccountId = String(userDoc.activeAccountId || userDoc.accountId || "");
  const plan = normalizePlan(userDoc.plan || userDoc.planCode || "full");
  return {
    activeAccountId,
    accountId: String(userDoc.accountId || activeAccountId || ""),
    role: ["owner", "admin", "staff", "viewer", "super_admin"].includes(userDoc.role)
      ? userDoc.role
      : "owner",
    plan,
    isActive: userDoc.isActive !== false,
    email: String(userDoc.email || authUser?.email || ""),
    displayName: String(userDoc.displayName || authUser?.displayName || ""),
  };
}

function normalizeEmail(value = "") {
  return String(value || "").trim().toLowerCase();
}

function emailInviteKey(value = "") {
  return normalizeEmail(value).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function normalizeAccount(accountDoc = {}, fallback = {}) {
  const rawBillingPlan = String(
    accountDoc.billingPlan ||
      accountDoc.subscriptionPlan ||
      accountDoc.plan ||
      fallback.billingPlan ||
      fallback.subscriptionPlan ||
      fallback.plan ||
      "trial"
  );
  const accessPlan = resolveAccessPlan(
    rawBillingPlan,
    accountDoc.accessPlan ||
      accountDoc.featurePlan ||
      accountDoc.planCode ||
      fallback.accessPlan ||
      fallback.featurePlan ||
      fallback.planCode ||
      ""
  );
  const accountStatus = normalizeAccountStatus(
    accountDoc.accountStatus || accountDoc.subscriptionStatus || accountDoc.status || "",
    accountDoc.isActive !== false
  );
  const isActive =
    accountDoc.isActive != null
      ? accountDoc.isActive === true
      : isAccountStatusUnlocked(accountStatus);
  return {
    id: String(accountDoc.id || fallback.id || ""),
    name: String(accountDoc.name || fallback.name || "FabriQuote Workspace"),
    ownerUid: String(accountDoc.ownerUid || fallback.ownerUid || ""),
    dataOwnerUid: String(
      accountDoc.dataOwnerUid ||
        accountDoc.workspaceOwnerUid ||
        fallback.dataOwnerUid ||
        fallback.workspaceOwnerUid ||
        accountDoc.ownerUid ||
        fallback.ownerUid ||
        ""
    ),
    plan: accessPlan,
    planCode: accessPlan,
    billingPlan: normalizeBillingPlan(rawBillingPlan),
    accountStatus,
    isActive,
    subscriptionStatus: accountStatus,
    trialQuotationLimit: Number(accountDoc.trialQuotationLimit || 0) || 0,
    trialQuotationsUsed: Number(accountDoc.trialQuotationsUsed || 0) || 0,
    createdVia: String(accountDoc.createdVia || fallback.createdVia || ""),
    trialStartedAt: accountDoc.trialStartedAt || null,
    trialExhaustedAt: accountDoc.trialExhaustedAt || null,
    activatedAt: accountDoc.activatedAt || null,
    activatedBy: String(accountDoc.activatedBy || ""),
    subscriptionValidUntil: accountDoc.subscriptionValidUntil || null,
    manualPaymentNotes: String(accountDoc.manualPaymentNotes || ""),
    lockedReason: String(accountDoc.lockedReason || ""),
    featureFlags: normalizeFeatureMap(accountDoc.features || accountDoc.featureFlags, accessPlan),
    features: normalizeFeatureMap(accountDoc.features || accountDoc.featureFlags, accessPlan),
    staffLimit: normalizeStaffLimit(accountDoc.staffLimit, DEFAULT_STAFF_LIMIT),
    activeStaffCount: normalizeStaffLimit(accountDoc.activeStaffCount, 0),
    extraStaffPurchased: normalizeExtraStaffPurchased(accountDoc.extraStaffPurchased),
    createdAt: accountDoc.createdAt || null,
    updatedAt: accountDoc.updatedAt || null,
  };
}

function normalizeMember(memberDoc = {}, account = null, userDoc = {}) {
  const plan = normalizePlan(memberDoc.plan || userDoc.plan || account?.plan || "full");
  const features = normalizeFeatureMap(
    memberDoc.features || memberDoc.featureFlags || account?.features || account?.featureFlags,
    plan
  );
  return {
    uid: String(memberDoc.uid || ""),
    email: String(memberDoc.email || userDoc.email || ""),
    role: canonicalMemberRole(memberDoc),
    plan,
    isActive: memberDoc.isActive === true && (!memberDoc.status || memberDoc.status === 'active'),
    permissions: canonicalMemberPermissions(memberDoc),
    features,
    createdAt: memberDoc.createdAt || memberDoc.addedAt || null,
    updatedAt: memberDoc.updatedAt || null,
  };
}

export function AccountProvider({ children }) {
  const { currentUser } = useAuth();
  const [employeeWorkspaces, setEmployeeWorkspaces] = useState([]);
  const accessRunRef = useRef(0);
  const actorUidRef = useRef(null);
  const [accountState, setAccountState] = useState({
    loading: true,
    account: null,
    accountUser: null,
    isSuperAdmin: false,
    error: "",
    debug: null,
  });

  const refreshAccount = useCallback(async () => {
    const runId = ++accessRunRef.current;
    if (!currentUser?.uid) {
      actorUidRef.current = null;
      setEmployeeWorkspaces([]);
      setAccountState({
        loading: false,
        account: null,
        accountUser: null,
        isSuperAdmin: false,
        error: "",
        debug: null,
      });
      return;
    }

    const sameActor = actorUidRef.current === currentUser.uid;
    actorUidRef.current = currentUser.uid;
    if (!sameActor) setEmployeeWorkspaces([]);
    // Preserve the editor only for same-identity background refreshes.
    setAccountState(prev => sameActor ? { ...prev, loading: !prev.account, error: '', debug: null }
      : { loading: true, account: null, accountUser: null, isSuperAdmin: false, error: '', debug: null });

    let isSuperAdmin = false;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      const employeeAccess = await refreshEmployeeAccess(currentUser);
      if (accessRunRef.current !== runId) return;
      if (employeeAccess) setEmployeeWorkspaces(employeeAccess.workspaces || []);
      if (employeeAccess?.identityVerificationRequired) {
        throw new Error('Verify your email using the setup email, then choose Try again. Your assigned permissions will become available automatically.');
      }
      if (EMPLOYEE_PROVISIONING_V2) invalidateFirestoreReadCache(userRef);
      const attachPendingInvite = async () => {
        const normalizedEmail = normalizeEmail(currentUser.email || "");
        if (!normalizedEmail) return null;

        const pendingRef = doc(db, "pendingAccountInvites", emailInviteKey(normalizedEmail));
        const pendingSnap = await trackedGetDoc(pendingRef, {
          label: "accountPendingInvite",
        });
        if (!pendingSnap.exists()) return null;

        const pending = pendingSnap.data() || {};
        if (EMPLOYEE_PROVISIONING_V2 && !currentUser.emailVerified) {
          throw new Error('Please verify your email before opening the workspace assigned to you.');
        }
        const accountId = String(pending.accountId || "");
        if (!accountId) return null;

        const role = pending.role || "owner";
        const plan = normalizePlan(pending.plan || "full");
        const features = normalizeFeatureMap(pending.features, plan);
        const isActive = pending.isActive !== false;

        await setDoc(
          userRef,
          {
            accountId,
            activeAccountId: accountId,
            email: normalizedEmail,
            role,
            plan,
            isActive,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );

        await setDoc(
          doc(db, "accounts", accountId, "members", currentUser.uid),
          {
            uid: currentUser.uid,
            email: normalizedEmail,
            role,
            plan,
            isActive,
            features,
            addedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        await deleteDoc(pendingRef);
        return {
          accountId,
          role,
          plan,
          isActive,
          email: normalizedEmail,
        };
      };
      try {
        const superAdminSnap = await trackedGetDoc(
          doc(db, "superAdmins", currentUser.uid),
          { label: "accountSuperAdminCheck" }
        );
        const superAdminData = superAdminSnap.exists() ? superAdminSnap.data() : {};
        isSuperAdmin =
          superAdminSnap.exists() &&
          (superAdminData.role === "super_admin" || superAdminData.active !== false);
      } catch (superAdminError) {
        isSuperAdmin = false;
      }

      let userSnap;
      try {
        userSnap = await trackedGetDoc(userRef, {
          label: "accountUserRoot",
        });
      } catch (readError) {
        throw buildBootstrapError("permission_denied", "Firestore permission denied while reading /users/{uid}.", {
          lookup: `/users/${currentUser.uid}`,
          originalMessage: readError?.message || "",
        });
      }

      if (!userSnap.exists()) {
        const attachedUser = await attachPendingInvite();
        if (attachedUser) {
          userSnap = await trackedGetDoc(userRef, {
            label: "accountUserRootAfterInviteAttach",
          });
        }
      }

      if (!userSnap.exists()) {
        if (isSuperAdmin) {
          setAccountState({
            loading: false,
            account: null,
            accountUser: {
              activeAccountId: "",
              accountId: "",
              role: "super_admin",
              plan: "full",
              isActive: true,
              email: currentUser.email || "",
              displayName: currentUser.displayName || "",
              features: getFeatureAccess("full"),
            },
            isSuperAdmin: true,
            error: "",
            debug: {
              status: "super_admin_without_account",
              userPath: `/users/${currentUser.uid}`,
            },
          });
          return;
        }

        throw buildBootstrapError("user_doc_missing", "User doc missing at /users/{uid}.", {
          lookup: `/users/${currentUser.uid}`,
        });
      }

      const accountUser = normalizeUserRoot(userSnap.data(), currentUser);
      let accountId = accountUser.activeAccountId || accountUser.accountId;

      if (!accountId) {
        const attachedUser = await attachPendingInvite();
        if (attachedUser) {
          userSnap = await trackedGetDoc(userRef, {
            label: "accountUserRootAfterPendingInvite",
          });
          const refreshedUser = normalizeUserRoot(userSnap.data(), currentUser);
          accountId = refreshedUser.activeAccountId || refreshedUser.accountId;
        }

        if (!accountId) {
          throw buildBootstrapError("account_id_missing", "Account id missing. Expected users/{uid}.activeAccountId or users/{uid}.accountId.", {
            lookup: `/users/${currentUser.uid}`,
            userDoc: userSnap.data(),
          });
        }
      }

      const accountRef = doc(db, "accounts", accountId);
      let accountSnap;
      try {
        accountSnap = await trackedGetDoc(accountRef, {
          label: "accountRoot",
        });
      } catch (readError) {
        throw buildBootstrapError("permission_denied", "Firestore permission denied while reading /accounts/{accountId}.", {
          lookup: `/accounts/${accountId}`,
          originalMessage: readError?.message || "",
        });
      }

      if (!accountSnap.exists()) {
        throw buildBootstrapError("account_doc_missing", "Account doc missing at /accounts/{accountId}.", {
          lookup: `/accounts/${accountId}`,
        });
      }

      const memberRef = doc(db, "accounts", accountId, "members", currentUser.uid);
      if (EMPLOYEE_PROVISIONING_V2) invalidateFirestoreReadCache(memberRef);
      let memberSnap;
      try {
        memberSnap = await trackedGetDoc(memberRef, {
          label: "accountMember",
        });
      } catch (readError) {
        throw buildBootstrapError("permission_denied", "Firestore permission denied while reading /accounts/{accountId}/members/{uid}.", {
          lookup: `/accounts/${accountId}/members/${currentUser.uid}`,
          originalMessage: readError?.message || "",
        });
      }

      if (!memberSnap.exists()) {
        await attachPendingInvite();
        memberSnap = await trackedGetDoc(memberRef, {
          label: "accountMemberAfterInviteAttach",
        });

        if (!memberSnap.exists()) {
          throw buildBootstrapError("member_doc_missing", "Member doc missing at /accounts/{accountId}/members/{uid}.", {
            lookup: `/accounts/${accountId}/members/${currentUser.uid}`,
          });
        }
      }

      const account = normalizeAccount({ ...accountSnap.data(), id: accountSnap.id });
      const member = normalizeMember(memberSnap.data() || {}, account, userSnap.data() || {});

      if (member.isActive !== true || accountUser.isActive !== true) {
        throw buildBootstrapError("member_inactive", "Member doc exists, but isActive is not true.", {
          lookup: `/accounts/${accountId}/members/${currentUser.uid}`,
          accountIsActive: account.isActive,
          userIsActive: accountUser.isActive,
          memberIsActive: member.isActive,
        });
      }

      if (accessRunRef.current !== runId) return;
      setAccountState({
        loading: false,
        account,
        accountUser: {
          ...accountUser,
          activeAccountId: accountId,
          accountId,
          member,
          role: member.role || accountUser.role || "owner",
          plan: member.plan || accountUser.plan || account.plan,
          features: member.features || account.features,
        },
        isSuperAdmin,
        error: "",
        debug: {
          status: "ok",
          userPath: `/users/${currentUser.uid}`,
          accountPath: `/accounts/${accountId}`,
          memberPath: `/accounts/${accountId}/members/${currentUser.uid}`,
        },
      });
    } catch (error) {
      if (accessRunRef.current !== runId) return;
      const debug = error?.debug || {
        code: "unknown",
        originalMessage: error?.message || "",
      };
      let customClaims = {};
      try {
        customClaims = (await currentUser.getIdTokenResult(false))?.claims || {};
      } catch (claimsError) {
        customClaims = {
          error: claimsError?.message || "Could not read token claims.",
        };
      }
      if (IS_DEV_UI_ENABLED) {
        devLog("ACCOUNT_LOAD_DEBUG", {
          authUid: currentUser.uid,
          email: currentUser.email || "",
          customClaims,
          attemptedUserDocPaths: [`/users/${currentUser.uid}`],
          attemptedWorkspacePaths: debug.lookup ? [debug.lookup] : [],
          membershipDocPaths: debug.lookup?.includes("/members/") ? [debug.lookup] : [],
          errorCode: debug.code || error?.code || "",
          errorMessage: error?.message || "",
          debug,
        });
      }
      if (accessRunRef.current !== runId) return;
      setAccountState({
        loading: false,
        account: null,
        accountUser: null,
        isSuperAdmin,
        error: getBootstrapUserMessage(error),
        debug: {
          authUid: currentUser.uid,
          email: currentUser.email || "",
          customClaims,
          ...debug,
        },
      });
    }
  }, [currentUser]);

  useEffect(() => {
    refreshAccount();
  }, [refreshAccount]);

  useEffect(() => {
    if (!EMPLOYEE_PROVISIONING_V2 || !currentUser?.uid) return undefined;
    let lastRefresh = Date.now();
    const onFocus = () => {
      if (Date.now() - lastRefresh < 60000) return;
      lastRefresh = Date.now();
      refreshAccount();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [currentUser?.uid, refreshAccount]);

  const selectEmployeeWorkspace = useCallback(async choice => {
    if (!window.confirm('Switch workspace? Save any unsaved quotation changes first. Continue?')) return;
    const previous = accountState;
    const actor = currentUser?.uid;
    setAccountState({ loading: true, account: null, accountUser: null, isSuperAdmin: false, error: '', debug: null });
    try {
      await httpsCallable(functions, 'selectEmployeeWorkspace')({ accountId: choice.accountId, workspaceUid: choice.workspaceUid });
      if (actorUidRef.current === actor) await refreshAccount();
    } catch (error) {
      if (actorUidRef.current === actor) setAccountState(previous);
      throw error;
    }
  }, [refreshAccount, accountState, currentUser?.uid]);

  const value = useMemo(() => {
    const sameIdentity = actorUidRef.current === (currentUser?.uid || null);
    const visibleState = sameIdentity ? accountState : { loading: true, account: null, accountUser: null,
      isSuperAdmin: false, error: '', debug: null };
    const account = visibleState.account;
    const member = visibleState.accountUser?.member || null;
    const subscriptionStatus = account?.accountStatus || (account?.isActive ? "active" : "inactive");
    const hasActiveSubscription =
      isAccountStatusUnlocked(subscriptionStatus) &&
      account?.isActive === true &&
      visibleState.accountUser?.isActive !== false &&
      member?.isActive !== false;
    const flags = normalizeFeatureMap(
      visibleState.accountUser?.features || member?.features || account?.features,
      visibleState.accountUser?.plan || member?.plan || account?.plan || "full"
    );

    const legacyFlags = Object.entries(LEGACY_FEATURE_ALIASES).reduce(
      (next, [legacyKey, camelKey]) => ({
        ...next,
        [legacyKey]: flags[camelKey] === true,
      }),
      {}
    );

    const hasFeature = (featureKey) =>
      hasActiveSubscription && hasFeatureAccess(flags, featureKey);
    const workspaceUid =
      account?.dataOwnerUid ||
      account?.ownerUid ||
      currentUser?.uid ||
      "";

    return {
      ...visibleState,
      currentAccount: account,
      currentUser,
      workspaceUid,
      subscriptionStatus,
      hasActiveSubscription,
      isTrialAccount: account?.accountStatus === "trial",
      isLockedAccount: !isAccountStatusUnlocked(subscriptionStatus),
      trialQuotationLimit: account?.trialQuotationLimit || 0,
      trialQuotationsUsed: account?.trialQuotationsUsed || 0,
      trialQuotationsRemaining: Math.max(
        0,
        Number(account?.trialQuotationLimit || 0) - Number(account?.trialQuotationsUsed || 0)
      ),
      featureFlags: { ...legacyFlags, ...flags },
      features: flags,
      defaultFeatures: getFeatureAccess(visibleState.accountUser?.plan || account?.plan || "full"),
      hasFeature,
      refreshAccount,
      employeeWorkspaces: sameIdentity ? employeeWorkspaces : [],
      selectEmployeeWorkspace,
    };
  }, [accountState, currentUser, refreshAccount, employeeWorkspaces, selectEmployeeWorkspace]);

  return <AccountContext.Provider value={value}>
    {EMPLOYEE_PROVISIONING_V2 && currentUser && actorUidRef.current === currentUser.uid && (employeeWorkspaces.length > 1
      || (employeeWorkspaces.length === 1 && employeeWorkspaces[0].accountId !== accountState.accountUser?.activeAccountId)) ? (
      <aside aria-label="Workspace selection" style={{ padding: '8px 16px', background: '#f1f5f9', display: 'flex', gap: 8, alignItems: 'center' }}>
        <label htmlFor="employee-workspace">Workspace</label>
        <select id="employee-workspace" value={accountState.accountUser?.activeAccountId || ''}
          onChange={event => {
            const choice = employeeWorkspaces.find(item => item.accountId === event.target.value);
            if (choice) selectEmployeeWorkspace(choice).catch(() => setAccountState(previous => ({ ...previous, error: 'Could not switch workspace. Please try again.' })));
          }}>
          <option value="" disabled>Choose workspace</option>
          {employeeWorkspaces.map(item => <option key={item.accountId} value={item.accountId}>{item.name || 'Workspace'}</option>)}
        </select>
      </aside>
    ) : null}
    {children}
  </AccountContext.Provider>;
}

function buildBootstrapError(code, message, debug = {}) {
  const error = new Error(message);
  error.debug = {
    code,
    ...debug,
  };
  return error;
}

function getBootstrapUserMessage(error) {
  const code = String(error?.debug?.code || error?.code || "").toLowerCase();
  if (code === "user_doc_missing" || code === "account_id_missing" || code === "member_doc_missing") {
    return "Your login exists, but it is not connected to a FabriQuote workspace yet. Please contact your admin.";
  }
  if (code === "permission_denied" || code.includes("permission-denied")) {
    return "Your account does not have permission to access this workspace.";
  }
  if (code === "account_doc_missing") {
    return "Workspace setup is incomplete. Please contact your admin.";
  }
  if (code === "member_inactive") {
    const debug = error?.debug || {};
    if (debug.accountIsActive === false) {
      return "This workspace is inactive. Please contact support.";
    }
    return "Your workspace access is inactive. Please contact your admin.";
  }
  return error?.message || "Could not load account access.";
}
